import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, Plus, Trash2, ChevronDown, X } from "lucide-react";
import { WaterEntry, PollutantEntry, PollutantDocWithId } from "@/types/water";
import { useState, useRef, useEffect } from "react";
import { normalizeText } from "@/utils/textFormatter";

interface WaterSludgeTabProps {
  formEntry: WaterEntry;
  onFormEntryChange: (entry: WaterEntry) => void;
  pollutants: PollutantDocWithId[];
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string) => void;
}

export const WaterSludgeTab = ({
  formEntry,
  onFormEntryChange,
  pollutants,
  errors,
  touched,
  onFieldChange,
  onFieldBlur,
}: WaterSludgeTabProps) => {
  const { t } = useLanguage();
  
  // State for custom dropdowns
  const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>({});
  const [searchValues, setSearchValues] = useState<Record<number, string>>({});
  const [isScrolling, setIsScrolling] = useState(false);
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const handleSludgeQuantityChange = (value: string) => {
    const quantity: number | "" = value === "" ? "" : parseFloat(value);
    const updatedEntry = {
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        sludgeQuantity: quantity,
        sludgeUnit: "t",
      },
    };
    onFormEntryChange(updatedEntry);
    onFieldChange("sludgeQuantity", value);
  };

  const handleMoistureContentChange = (value: string) => {
    onFieldChange("moistureContent", value);
    onFormEntryChange({
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        moistureContent: value === "" ? "" : parseFloat(value),
      },
    });
  };

  const handleDepositionDecision = (value: string) => {
    const isDeposited = value === "no";
    onFieldChange("isDepositedInBasin", isDeposited);
    onFormEntryChange({
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        isDepositedInBasin: isDeposited,
        pollutants: isDeposited ? formEntry.sludge.pollutants || [] : [],
      },
    });
  };

  const handleAddSludgePollutant = () => {
    const newPollutant: PollutantEntry = {
      pollutantId: "",
      pollutantName: "",
      concentration: "",
      hasMonitoring: null,
      concentrationInReceiver: "",
    };

    const newIndex = (formEntry.sludge.pollutants || []).length;
    const updatedPollutants = [...(formEntry.sludge.pollutants || []), newPollutant];
    onFormEntryChange({
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        pollutants: updatedPollutants,
      },
    });

    // Clear any existing errors for the new pollutant
    onFieldChange(`sludgePollutant_${newIndex}`, "");
    onFieldChange(`sludgeConcentration_${newIndex}`, "");
    onFieldChange(`sludgeMonitoring_${newIndex}`, "");
    onFieldChange(`sludgeReceiverConcentration_${newIndex}`, "");
  };

  const handleRemoveSludgePollutant = (index: number) => {
    const updatedPollutants = (formEntry.sludge.pollutants || []).filter((_, i) => i !== index);
    onFormEntryChange({
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        pollutants: updatedPollutants,
      },
    });

    // Clear errors for the removed pollutant and reindex remaining ones
    onFieldChange(`sludgePollutant_${index}`, "");
    onFieldChange(`sludgeConcentration_${index}`, "");
    onFieldChange(`sludgeMonitoring_${index}`, "");
    onFieldChange(`sludgeReceiverConcentration_${index}`, "");

    // Clear errors for any pollutants that were after the removed one
    const originalLength = (formEntry.sludge.pollutants || []).length;
    for (let i = index + 1; i < originalLength; i++) {
      onFieldChange(`sludgePollutant_${i}`, "");
      onFieldChange(`sludgeConcentration_${i}`, "");
      onFieldChange(`sludgeMonitoring_${i}`, "");
      onFieldChange(`sludgeReceiverConcentration_${i}`, "");
    }
  };

  const handleSludgePollutantChange = (index: number, field: keyof PollutantEntry, value: any) => {
    const updatedPollutants = [...(formEntry.sludge.pollutants || [])];
    const pollutant = updatedPollutants[index];

    if (field === "pollutantId") {
      const selectedPollutant = pollutants.find((p) => p.id === value);
      pollutant.pollutantId = value;
      pollutant.pollutantName = selectedPollutant?.name || "";
      // Clear the error for this field when user makes a selection
      onFieldChange(`sludgePollutant_${index}`, value);
    } else if (field === "concentration") {
      pollutant.concentration = value === "" ? "" : parseFloat(value);
      // Clear the error for this field when user types
      onFieldChange(`sludgeConcentration_${index}`, value);
    } else if (field === "concentrationInReceiver") {
      pollutant.concentrationInReceiver = value === "" ? "" : parseFloat(value);
      // Clear the error for this field when user types
      onFieldChange(`sludgeReceiverConcentration_${index}`, value);
    } else {
      (pollutant as any)[field] = value;
    }

    onFormEntryChange({
      ...formEntry,
      sludge: {
        ...formEntry.sludge,
        pollutants: updatedPollutants,
      },
    });
  };

  const handleSludgePollutantMonitoringDecision = (index: number, value: string) => {
    const hasMonitoring = value === "yes";
    handleSludgePollutantChange(index, "hasMonitoring", hasMonitoring);

    // Clear the monitoring error when user makes a decision
    onFieldChange(`sludgeMonitoring_${index}`, hasMonitoring);

    if (!hasMonitoring) {
      handleSludgePollutantChange(index, "concentrationInReceiver", "");
      // Clear the receiver concentration error when monitoring is disabled
      onFieldChange(`sludgeReceiverConcentration_${index}`, "");
    }
  };

  // Get search value for a pollutant
  const getSearchValue = (index: number, pollutant: PollutantEntry) => {
    if (searchValues[index] !== undefined) {
      return searchValues[index];
    }
    return pollutant.pollutantId 
      ? pollutants.find(p => p.id === pollutant.pollutantId)?.name || ""
      : "";
  };

  // Handle search input change
  const handleSearchChange = (index: number, value: string) => {
    setSearchValues(prev => ({ ...prev, [index]: value }));
    setOpenDropdowns(prev => ({ ...prev, [index]: true }));
  };

  // Handle search input click
  const handleSearchClick = (index: number) => {
    setOpenDropdowns(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Handle pollutant selection
  const handleSludgePollutantSelect = (index: number, pollutant: { id: string; name: string }) => {
    handleSludgePollutantChange(index, "pollutantId", pollutant.id);
    setSearchValues(prev => ({ ...prev, [index]: pollutant.name }));
    setOpenDropdowns(prev => ({ ...prev, [index]: false }));
  };

  // Handle clear selection
  const handleClearSelection = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    handleSludgePollutantChange(index, "pollutantId", "");
    setSearchValues(prev => ({ ...prev, [index]: "" }));
    setOpenDropdowns(prev => ({ ...prev, [index]: false }));
  };

  // Handle clicks outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is specifically on the modal scrollbar
      const modalScrollableArea = document.querySelector('[class*="overflow-y-auto"]');
      if (modalScrollableArea) {
        const modalRect = modalScrollableArea.getBoundingClientRect();
        // Only protect the scrollbar area
        const isOnModalScrollbar = 
          event.clientX > modalRect.right - 20 && 
          event.clientX <= modalRect.right && 
          event.clientY >= modalRect.top && 
          event.clientY <= modalRect.bottom;
        
        if (isOnModalScrollbar) {
          return;
        }
      }

      Object.keys(dropdownRefs.current).forEach(key => {
        const index = parseInt(key);
        const dropdownElement = dropdownRefs.current[index];
        
        if (!dropdownElement) return;
        
        // Check if click is within the dropdown bounds
        const rect = dropdownElement.getBoundingClientRect();
        const isWithinDropdownBounds = event.clientX >= rect.left && event.clientX <= rect.right && 
                                       event.clientY >= rect.top && event.clientY <= rect.bottom;
        
        if (isWithinDropdownBounds) {
          return;
        }
        
        // Only close if click is truly outside and not currently scrolling
        if (!dropdownElement.contains(event.target as Node) && !isScrolling) {
          setOpenDropdowns(prev => ({
            ...prev,
            [index]: false
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isScrolling]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDropdowns({});
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="space-y-6">
      {/* Sludge Configuration */}
      <div className="space-y-6">
        {/* Sludge Quantity and Moisture Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className={`text-base ${errors.sludgeQuantity ? "text-red-500" : "text-gray-700"}`}>
              {t("water.sludgeQuantity")} *
            </Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formEntry.sludge.sludgeQuantity === "" ? "" : formEntry.sludge.sludgeQuantity}
                onChange={(e) => handleSludgeQuantityChange(e.target.value)}
                onBlur={() => onFieldBlur("sludgeQuantity")}
                placeholder="0.00"
                className={`h-11 text-base pr-8 ${
                  touched.sludgeQuantity && errors.sludgeQuantity
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-500 text-sm">t</span>
              </div>
            </div>
            {errors.sludgeQuantity && (
              <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
                <Info className="h-3 w-3" />
                {errors.sludgeQuantity}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className={`text-base ${errors.moistureContent ? "text-red-500" : "text-gray-700"}`}>
              {t("water.moistureContent")} *
            </Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formEntry.sludge.moistureContent === "" ? "" : formEntry.sludge.moistureContent}
                onChange={(e) => handleMoistureContentChange(e.target.value)}
                onBlur={() => onFieldBlur("moistureContent")}
                placeholder="0.00"
                className={`h-11 text-base pr-8 ${
                  touched.moistureContent && errors.moistureContent
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-500 text-sm">%</span>
              </div>
            </div>
            {errors.moistureContent && (
              <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
                <Info className="h-3 w-3" />
                {errors.moistureContent}
              </div>
            )}
          </div>
        </div>

        {/* Deposition Question */}
        <div className="space-y-3">
          <Label className="text-base text-gray-700">{t("water.sludgeDepositionQuestion")} *</Label>
          <div className="flex gap-1 max-w-[200px]">
            <button
              type="button"
              onClick={() => handleDepositionDecision("no")}
              className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
                formEntry.sludge.isDepositedInBasin === true
                  ? "bg-red-600 text-white border-red-600 shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              {t("common.no")}
            </button>
            <button
              type="button"
              onClick={() => handleDepositionDecision("yes")}
              className={`flex-1 h-10 text-sm rounded-lg border transition-all duration-200 ${
                formEntry.sludge.isDepositedInBasin === false
                  ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              {t("common.yes")}
            </button>
          </div>
          {formEntry.sludge.isDepositedInBasin === null && errors.isDepositedInBasin && (
            <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
              <Info className="h-3 w-3" />
              {errors.isDepositedInBasin}
            </div>
          )}
        </div>

        {/* Pollutants Section */}
        {formEntry.sludge.isDepositedInBasin === true && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className={`text-base ${errors.sludgePollutants ? "text-red-500" : "text-gray-700"}`}>
                  {t("water.sludgePollutantsMonitoring")} *
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-gray-400 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>{t("water.sludgePollutantRequired")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Button
                type="button"
                onClick={handleAddSludgePollutant}
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t("common.add")}
              </Button>
            </div>

            {errors.sludgePollutants && (
              <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
                <Info className="h-3 w-3" />
                {errors.sludgePollutants}
              </div>
            )}

            {!formEntry.sludge.pollutants || formEntry.sludge.pollutants.length === 0 ? (
              <div className="py-6 px-4 text-center text-gray-500 text-sm bg-gray-25 rounded-lg border border-gray-200">
                <div className="flex items-center justify-center gap-1.5">
                  <Info className="h-4 w-4 text-gray-400" />
                  <span>{t("water.addPollutantsMessage")}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {formEntry.sludge.pollutants.map((pollutant, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* First Row: Pollutant Type, Concentration, Remove Button */}
                    <div className="grid grid-cols-12 gap-4 items-start mb-3">
                      {/* Pollutant Type - 7 columns */}
                      <div className="col-span-7">
                        <div className="flex items-center gap-2 mb-1">
                          <Label className="text-sm text-gray-700">{t("water.pollutantLabel")} *</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>{t("water.sludgePollutantTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="relative" ref={(el) => { dropdownRefs.current[index] = el; }}>
                          <div className="relative">
                            <input
                              type="text"
                              value={getSearchValue(index, pollutant)}
                              onChange={(e) => handleSearchChange(index, e.target.value)}
                              onClick={() => handleSearchClick(index)}
                              placeholder={t("water.selectPlaceholder")}
                              autoComplete="off"
                              spellCheck="false"
                              className={`w-full h-10 px-3 py-2 cursor-pointer pr-10 border border-gray-200 rounded-md outline-none focus:outline-none ${
                                touched[`sludgePollutant_${index}`] && errors[`sludgePollutant_${index}`]
                                  ? "border-red-500"
                                  : ""
                              }`}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                              {getSearchValue(index, pollutant) && (
                                <button
                                  type="button"
                                  onClick={(e) => handleClearSelection(index, e)}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                              <ChevronDown
                                className={`h-4 w-4 text-gray-500 transition-transform ${openDropdowns[index] ? "transform rotate-180" : ""}`}
                              />
                            </div>
                          </div>
                          
                          {openDropdowns[index] && (
                            <div
                              className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                              style={{ maxHeight: "320px", overflowY: "auto" }}
                              onMouseDown={(e) => {
                                // Prevent dropdown from closing when clicking on scrollbar
                                const target = e.currentTarget;
                                const rect = target.getBoundingClientRect();
                                const isScrollbarClick = e.clientX > rect.right - 16;
                                
                                if (isScrollbarClick) {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                              }}
                              onScroll={() => {
                                setIsScrolling(true);
                                setTimeout(() => setIsScrolling(false), 150);
                              }}
                            >
                              {pollutants
                                .filter((p) => {
                                  const searchValue = searchValues[index] || "";
                                  let matchesSearch = true;
                                  if (searchValue) {
                                    const normalizedPollutantName = normalizeText(p.name);
                                    const normalizedSearchValue = normalizeText(searchValue);
                                    matchesSearch = normalizedPollutantName.includes(normalizedSearchValue);
                                  }
                                  
                                  // Show the currently selected pollutant for this index
                                  if (p.id === pollutant.pollutantId) return matchesSearch;
                                  
                                  // Filter out pollutants already selected in sludge
                                  const isSelectedInSludge =
                                    formEntry.sludge.pollutants?.some(
                                      (selectedPollutant, selectedIndex) =>
                                        selectedIndex !== index && selectedPollutant.pollutantId === p.id
                                    ) || false;
                                  return !isSelectedInSludge && matchesSearch;
                                })
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSludgePollutantSelect(index, { id: p.id, name: p.name });
                                    }}
                                  >
                                    {p.name}
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                        {errors[`sludgePollutant_${index}`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`sludgePollutant_${index}`]}</p>
                        )}
                      </div>

                      {/* Concentration */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Label className="text-sm text-gray-700">{t("water.concentrationLabel")} *</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>{t("water.sludgeConcentrationTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={pollutant.concentration === "" ? "" : pollutant.concentration}
                            onChange={(e) => handleSludgePollutantChange(index, "concentration", e.target.value)}
                            onBlur={() => onFieldBlur(`sludgeConcentration_${index}`)}
                            placeholder="0.00"
                            className={`h-10 text-base pr-12 border-gray-200 hover:border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                              touched[`sludgeConcentration_${index}`] && errors[`sludgeConcentration_${index}`]
                                ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                                : ""
                            }`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">mg/L</span>
                        </div>
                        {errors[`sludgeConcentration_${index}`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`sludgeConcentration_${index}`]}</p>
                        )}
                      </div>

                      {/* Remove Button */}
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveSludgePollutant(index)}
                          className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 mt-4"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Second Row: Monitoring Question and Conditional Field */}
                    <div className="border-t border-gray-100 pt-3">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        {/* Monitoring Toggle */}
                        <div className="col-span-7">
                          <div className="flex items-start gap-2 mb-1">
                            <div className="flex-1 min-w-0">
                              <Label className="text-sm text-gray-700">{t("water.monitoringLabel")} *</Label>
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3 w-3 text-gray-400 cursor-help mt-1" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{t("water.sludgeMonitoringTooltip")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex gap-1 mt-2">
                            <button
                              type="button"
                              onClick={() => handleSludgePollutantMonitoringDecision(index, "no")}
                              className={`w-16 h-10 text-sm rounded-lg border transition-all duration-200 ${
                                pollutant.hasMonitoring === false
                                  ? "bg-red-600 text-white border-red-600 shadow-sm"
                                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                              }`}
                            >
                              {t("water.no")}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSludgePollutantMonitoringDecision(index, "yes")}
                              className={`w-16 h-10 text-sm rounded-lg border transition-all duration-200 ${
                                pollutant.hasMonitoring === true
                                  ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                              }`}
                            >
                              {t("water.yes")}
                            </button>
                          </div>
                          {errors[`sludgeMonitoring_${index}`] && (
                            <p className="text-xs text-red-500 mt-1">{errors[`sludgeMonitoring_${index}`]}</p>
                          )}
                        </div>

                        {/* Receiver Concentration (when monitoring is enabled) */}
                        {pollutant.hasMonitoring === true && (
                          <div className="col-span-5">
                            <div className="flex items-center gap-2 mb-1">
                              <Label className="text-sm text-gray-700">{t("water.receiverConcentrationLabel")} *</Label>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p>{t("water.sludgeReceiverConcentrationTooltip")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={pollutant.concentrationInReceiver === "" ? "" : pollutant.concentrationInReceiver}
                                onChange={(e) =>
                                  handleSludgePollutantChange(index, "concentrationInReceiver", e.target.value)
                                }
                                onBlur={() => onFieldBlur(`sludgeReceiverConcentration_${index}`)}
                                placeholder="0.00"
                                className={`h-10 text-base pr-12 bg-gray-50 border-gray-200 hover:border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                                  touched[`sludgeReceiverConcentration_${index}`] &&
                                  errors[`sludgeReceiverConcentration_${index}`]
                                    ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                                mg/L
                              </span>
                            </div>
                            {errors[`sludgeReceiverConcentration_${index}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors[`sludgeReceiverConcentration_${index}`]}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
